import { memo } from 'react';
import { Handle, Position, NodeProps, type Node } from '@xyflow/react';
import { ArrowRightStartOnRectangleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { DefinitionNodeData } from '../../../utils/definitionTransform';
import { EntryNode as EntryNodeType } from '../../../types/definition';
import { ValidationError } from '../../../utils/definitionValidation';

interface ExtendedNodeData extends DefinitionNodeData {
  validationErrors?: ValidationError[];
}

function EntryNodeComponent({ data, selected }: NodeProps<Node<ExtendedNodeData>>) {
  const node = data.originalNode as EntryNodeType;
  const errors = data.validationErrors?.filter(e => e.severity === 'error') || [];
  const warnings = data.validationErrors?.filter(e => e.severity === 'warning') || [];
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;
  const hasOutput = node.output && Object.keys(node.output).length > 0;

  const getBorderColor = () => {
    if (hasErrors) return 'border-red-500';
    if (selected) return 'border-green-500 ring-2 ring-green-200 dark:ring-green-800';
    return 'border-green-400 dark:border-green-600';
  };

  return (
    <div
      className={`
        px-4 py-3 rounded-lg border-2 bg-white dark:bg-gray-800 shadow-sm min-w-[160px]
        ${getBorderColor()}
      `}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !bg-green-500 !border-2 !border-white dark:!border-gray-800"
      />

      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md ${hasErrors ? 'bg-red-100 dark:bg-red-900' : 'bg-green-100 dark:bg-green-900'}`}>
          <ArrowRightStartOnRectangleIcon className={`w-4 h-4 ${hasErrors ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex items-center gap-1">
            {data.label}
            {(hasErrors || hasWarnings) && (
              <ExclamationCircleIcon className={`w-4 h-4 ${hasErrors ? 'text-red-500' : 'text-amber-500'}`} />
            )}
          </div>
          <div className="flex items-center gap-1">
            {node.entry && (
              <span className="text-xs text-green-600 dark:text-green-400">Entry Point</span>
            )}
            {hasOutput && (
              <span className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300">
                Exit
              </span>
            )}
          </div>
        </div>
      </div>

      {node.prompt && (
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 truncate">
          {node.prompt}
        </div>
      )}

      {hasErrors && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400 truncate">
          {errors[0].message}
        </div>
      )}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-green-500 !border-2 !border-white dark:!border-gray-800"
      />
    </div>
  );
}

export const EntryNode = memo(EntryNodeComponent);
